/**
 * Configurable Order Queue batch scopes (BLU-777).
 *
 * Per-ORG, admin-editable named buttons, stored as a JSONB array at
 * `prod_settings.batch_actions`. Rendered as CHECKBOXES inside the queue's
 * `Create Batch (N) ▾` menu; ticking several yields a DEDUPED UNION of the
 * orders they match. Mirrors `picklist-sort.ts` — a pure resolver over an
 * org-supplied JSONB doc, with hand-written TS types AND a separate Zod schema
 * (this duplication is the repo's own pattern for these modules).
 *
 * ⚠ `sanitizeBatchActions` MUST run on every READ. This is a correctness
 * requirement, not typing hygiene: `prod_settings` carries
 * `GRANT ... TO authenticated` and its RLS insert/update policies pass for any
 * org Admin, so an Admin can PATCH arbitrary JSON straight through PostgREST —
 * and this document decides which orders get batched. Follow `picklist_sort`
 * here, NOT `hotkeys` (which returns raw).
 *
 * ⚠ TAG MATCHING IS EXACT, CASE-SENSITIVE — deliberately. The queue's existing
 * server-side tag filter is `.overlaps('tags', [...])`, i.e. Postgres `&&`,
 * which is exact. If this matcher case-folded, a scope's count could exceed
 * what the equivalent tag filter shows on screen, and the menu would promise
 * orders the queue cannot display. The settings UI sources tags from the org's
 * real tag vocabulary (`searchOrderTags`), so operators pick actual values
 * rather than typing them.
 */
import { z } from 'zod';
export const BATCH_ACTION_BUILTIN_KEYS = [
    'priority',
    'resolved_oos',
    'express',
];
// ---------------------------------------------------------------------------
// Caps. Shared by the sanitizer AND the Zod schema — keep the two in lockstep.
// ---------------------------------------------------------------------------
/** Menu length. A dropdown is the surface; past ~12 it stops being scannable. */
export const BATCH_ACTIONS_MAX = 12;
/** Twelve editable custom scopes plus the always-available system scope. */
export const EFFECTIVE_BATCH_ACTIONS_MAX = BATCH_ACTIONS_MAX + 1;
export const EXPRESS_BATCH_ACTION_ID = 'system:express';
export const EXPRESS_BATCH_ACTION = {
    id: EXPRESS_BATCH_ACTION_ID,
    name: 'Express',
    rule: { type: 'builtin', key: 'express' },
};
/** Tags per rule. Generous — covers a concept spelled several ways plus variants. */
export const BATCH_ACTION_MAX_TAGS = 12;
export const BATCH_ACTION_MAX_NAME_LENGTH = 40;
/** Matches the tag-length ceiling used elsewhere for Shopify tags. */
export const BATCH_ACTION_MAX_TAG_LENGTH = 100;
export const BATCH_ACTION_MAX_ID_LENGTH = 64;
/**
 * Ceiling on orders a single scoped batch may create, applied BATCH-WIDE to the
 * whole ticked union — tick three scopes totalling 300 and you get 200, not 600.
 * Brett, 2026-08-12: "a 200 order cap to keep things safe, and that just batch
 * wide."
 *
 * ⚠ This TRIMS (oldest first), it never throws. `MAX_ORDERS_PER_BATCH` (500) is
 * enforced at three layers and every one of them throws; nothing in the stack
 * truncates. Live unbatched-NEW counts make the trim path a day-one certainty,
 * not a guard rail: `custom_product` 456, `front and back` 155.
 *
 * ⚠ Deliberately a CONSTANT and not `prod_settings.max_batch_size`. That column
 * looks like the right home (documented "Maximum orders allowed per batch
 * (10-200)", CHECK 10..200) but it is DEAD — read nowhere in the
 * production-orders router/handler/service/dao — and its value on prod is 50 for
 * every org. Wiring it would silently give a 50-order cap AND retroactively
 * tighten every existing manual batch path. Making that column mean something is
 * its own ticket.
 */
export const MAX_SCOPED_BATCH_ORDERS = 200;
/**
 * No custom scopes configured. **Empty on purpose** — Express is supplied by
 * getEffectiveBatchActions, and every org
 * (Thread Lab, Mini Zali, Maku all ship empty `filter_tags`) starts clean
 * rather than inheriting Threadheads' tag vocabulary as dead buttons that
 * match nothing.
 *
 * Paired with `prod_settings.batch_actions DEFAULT NULL`: a row-less org and a
 * column-omitted row both resolve here, so the first unrelated settings save
 * cannot change behaviour (the BLU-656 trap).
 */
export const DEFAULT_BATCH_ACTIONS = [];
// ---------------------------------------------------------------------------
// Matcher.
// ---------------------------------------------------------------------------
/** `prod_orders.priority_reason` value written by the TS OOS-resolve paths. */
export const PRIORITY_REASON_OOS_RESOLVED = 'oos_resolved';
/**
 * Does this order fall in the scope?
 *
 * `resolved_oos` tests the EXACT reason column, not a resolved-issue join. The
 * join was ~88% precise (22 of 25 sampled priority orders had a matching
 * resolved issue), which means a button labelled "Resolved OOS" would quietly
 * batch orders that were priority for an unrelated reason. Rows written before
 * BLU-777 carry a best-effort backfill of the same join, so history is
 * approximate and everything after deploy is exact — one code path either way.
 */
export function matchesBatchActionRule(rule, order) {
    if (rule.type === 'builtin') {
        switch (rule.key) {
            case 'express':
                return order.isExpress === true;
            case 'priority':
                return order.isPriority;
            case 'resolved_oos':
                return order.isPriority && order.priorityReason === PRIORITY_REASON_OOS_RESOLVED;
            default:
                return false;
        }
    }
    return matchesExactTags(rule.tags, order.tags);
}
/** ANY-of, case-sensitive tag matching, shared with Express classification. */
export function matchesExactTags(tags, orderTags) {
    if (!orderTags || orderTags.length === 0)
        return false;
    // ANY-of, exact match — mirrors Postgres `&&` (see the file docblock on why
    // this must NOT case-fold).
    return tags.some((t) => orderTags.includes(t));
}
// ---------------------------------------------------------------------------
// Sanitizer.
// ---------------------------------------------------------------------------
export function sanitizeTagList(raw) {
    if (!Array.isArray(raw))
        return null;
    const seen = new Set();
    const tags = [];
    // Pre-slice before filter/map: nothing at the DB layer bounds this array, so
    // a direct PostgREST write can store an arbitrarily large one and every read
    // would iterate the stored size (within-tenant self-DoS). 4× the cap leaves
    // headroom for entries the filter drops.
    for (const value of raw.slice(0, BATCH_ACTION_MAX_TAGS * 4)) {
        if (tags.length >= BATCH_ACTION_MAX_TAGS)
            break;
        if (typeof value !== 'string')
            continue;
        const trimmed = value.trim();
        if (trimmed.length === 0 || trimmed.length > BATCH_ACTION_MAX_TAG_LENGTH)
            continue;
        // Exact dedupe only — case is significant to `&&`, so `B2B` and `b2b` are
        // genuinely different tags and both may legitimately be listed.
        if (seen.has(trimmed))
            continue;
        seen.add(trimmed);
        tags.push(trimmed);
    }
    return tags.length > 0 ? tags : null;
}
/**
 * BLU-1410: the tags of `a` that are also in `b`, compared trimmed and
 * case-insensitively, the way the DTF and DTG order tag lists match orders (a
 * tag in both lists would make every order carrying it a conflict). In `a`'s
 * order, trimmed, one per key. Empty when the lists share no tag.
 */
export function findTagOverlap(a, b) {
    const key = (tag) => tag.trim().toLowerCase();
    const inB = new Set(b.map(key));
    const seen = new Set();
    const overlap = [];
    for (const tag of a) {
        const k = key(tag);
        if (k.length === 0 || !inB.has(k) || seen.has(k))
            continue;
        seen.add(k);
        overlap.push(tag.trim());
    }
    return overlap;
}
function sanitizeRule(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const obj = raw;
    if (obj.type === 'builtin') {
        if (typeof obj.key === 'string' &&
            BATCH_ACTION_BUILTIN_KEYS.includes(obj.key)) {
            return { type: 'builtin', key: obj.key };
        }
        return null;
    }
    if (obj.type === 'tags') {
        const tags = sanitizeTagList(obj.tags);
        // A tag rule with no usable tags is DROPPED, not kept-as-empty: an empty
        // list matches nothing, so keeping it would render a button that silently
        // does nothing when pressed.
        return tags ? { type: 'tags', tags } : null;
    }
    return null;
}
function sanitizeAction(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const obj = raw;
    if (typeof obj.id !== 'string')
        return null;
    const id = obj.id.trim();
    if (id.length === 0 || id.length > BATCH_ACTION_MAX_ID_LENGTH)
        return null;
    if (typeof obj.name !== 'string')
        return null;
    const name = obj.name.trim();
    if (name.length === 0)
        return null;
    const rule = sanitizeRule(obj.rule);
    if (!rule || id === EXPRESS_BATCH_ACTION_ID ||
        (rule.type === 'builtin' && rule.key === 'express'))
        return null;
    return { id, name: name.slice(0, BATCH_ACTION_MAX_NAME_LENGTH), rule };
}
/**
 * Sanitize an untrusted/raw stored value into a safe `BatchAction[]`.
 *
 * Drops malformed entries rather than throwing (one bad hand-written entry must
 * not blank the whole menu), dedupes by `id` with first-occurrence-wins, and
 * caps the array. Returns a FRESH array. `null`/absent/garbage all resolve to
 * `DEFAULT_BATCH_ACTIONS` — which is empty, so a never-configured org and an
 * org whose entries were all invalid present identically: "no scopes
 * configured".
 */
export function sanitizeBatchActions(raw) {
    if (!Array.isArray(raw))
        return DEFAULT_BATCH_ACTIONS.map((a) => ({ ...a }));
    const actions = [];
    const seenIds = new Set();
    // Same pre-slice reasoning as sanitizeTagList.
    for (const rawAction of raw.slice(0, BATCH_ACTIONS_MAX * 4)) {
        if (actions.length >= BATCH_ACTIONS_MAX)
            break;
        const action = sanitizeAction(rawAction);
        if (!action || seenIds.has(action.id))
            continue;
        seenIds.add(action.id);
        actions.push(action);
    }
    return actions;
}
/** Compose every operational/presentation read from the same canonical scope. */
export function getEffectiveBatchActions(raw) {
    return [
        { ...EXPRESS_BATCH_ACTION, rule: { type: 'builtin', key: 'express' } },
        ...sanitizeBatchActions(raw),
    ];
}
// ---------------------------------------------------------------------------
// Zod schema for the tRPC write path. Carries the SAME caps as the sanitizer —
// keep the two in lockstep. Structural violations are rejected LOUDLY at the
// boundary; the sanitizer then still runs on every read, because the boundary
// is not the only writer (PostgREST is).
// ---------------------------------------------------------------------------
export const BatchActionRuleSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('tags'),
        tags: z
            .array(z.string().trim().min(1).max(BATCH_ACTION_MAX_TAG_LENGTH))
            .min(1)
            .max(BATCH_ACTION_MAX_TAGS),
    }),
    z.object({
        type: z.literal('builtin'),
        key: z.enum(['priority', 'resolved_oos', 'express']),
    }),
]);
export const BatchActionSchema = z.object({
    id: z.string().trim().min(1).max(BATCH_ACTION_MAX_ID_LENGTH),
    name: z.string().trim().min(1).max(BATCH_ACTION_MAX_NAME_LENGTH),
    rule: BatchActionRuleSchema,
}).refine((action) => action.id !== EXPRESS_BATCH_ACTION_ID, {
    message: 'The system Express id cannot be persisted as a custom action',
}).refine((action) => action.rule.type !== 'builtin' || action.rule.key !== 'express', {
    message: 'Express is already available as a system scope',
});
export const BatchActionsSchema = z
    .array(BatchActionSchema)
    .max(BATCH_ACTIONS_MAX)
    .refine((actions) => new Set(actions.map((a) => a.id)).size === actions.length, {
    message: 'Batch action ids must be unique',
});
